import {
    FC,
    FormEvent,
    ReactElement,
    useRef,
    useState,
} from 'react';

import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

import { ErrorLabel } from '../../../components';
import { Button, RecaptchaDisclaimer, TextInput } from '../../../compositions';
import { FormProps } from '../../../entities/@forms/Form/Form';
import { NewsletterFormData } from '../../../entities/@forms/NewsletterForm/NewsletterForm';
import { scrollIntoViewIncludingNavigation } from '../../../helpers/scroll';
import { useTrans } from '../../../hooks';
import { NewsletterFormErrors, validateNewsletterFormData } from './validations';

import './NewsletterForm.scss';

interface NewsletterFormProps extends FormProps<NewsletterFormData> {
    className?: string;
}

export const NewsletterForm: FC<NewsletterFormProps> = ({
    isLoading,
    error,
    onSubmit,
    className = '',
}): ReactElement => {
    const trans = useTrans();
    const { executeRecaptcha } = useGoogleReCaptcha();

    const formRef = useRef<HTMLFormElement>(null);
    const [formErrors, setFormErrors] = useState<NewsletterFormErrors>({});
    const [botDetected, setBotDetected] = useState<boolean>(false);

    const [email, setEmail] = useState<string>('');
    const [firstName, setFirstName] = useState<string>('');

    const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault();

        if (!executeRecaptcha) {
            setBotDetected(true);
            return;
        }

        const token = await executeRecaptcha();

        const formData: NewsletterFormData = {
            recaptcha: token,
            email,
            firstName,
        };

        const [errors, hasErrors] = validateNewsletterFormData(formData);

        setFormErrors(errors);
        setBotDetected(!token);

        if (!hasErrors) {
            onSubmit(formData);
        } else {
            scrollIntoViewIncludingNavigation<HTMLFormElement>(formRef);
        }
    };

    return (
        <form
            ref={formRef}
            onSubmit={handleSubmit}
            className={`newsletter-form ${className}`}
        >
            <TextInput
                hasUnderline
                hideLabel
                required
                label={trans('containers.newsletterForm.firstName.label')}
                placeholder={trans('containers.newsletterForm.firstName.placeholder')}
                value={firstName}
                error={formErrors.firstName}
                disabled={isLoading}
                onChange={setFirstName}
                className="newsletter-form__input"
            />

            <TextInput
                hasUnderline
                hideLabel
                required
                type="email"
                inputMode="email"
                autoComplete="email"
                label={trans('containers.newsletterForm.email.label')}
                placeholder={trans('containers.newsletterForm.email.placeholder')}
                value={email}
                error={formErrors.email}
                disabled={isLoading}
                onChange={setEmail}
                className="newsletter-form__input"
            />

            <Button
                hasAnimation
                isSmall
                type="submit"
                text={trans('containers.newsletterForm.submit')}
                className="newsletter-form__submit-button"
            />

            <RecaptchaDisclaimer className="newsletter-form__recaptcha-disclaimer" />

            {botDetected && (
                <ErrorLabel
                    text={trans('errors.unknownError')}
                    className="newsletter-form__recaptcha-error"
                />
            )}

            {error && (
                <ErrorLabel
                    text={error}
                    className="newsletter-form__error-label"
                />
            )}
        </form>
    );
};
